import React, { memo, useRef, useState } from 'react';
import { Streamdown } from 'streamdown';
import { cn } from '../../../utils/classnames';
import { PrimaryButton, Popover } from '../../..';
import { Tag } from '../../tags/Tag';
import { Attachment, CloseOutline } from '@core/icons-react';
import { DataTile } from '../../tiles/DataTiles/DataTile';
import { InfoTile } from '../../tiles/CardTiles/InfoTile';
import { Link } from '../../interactives/Link';
import { CodeTile } from '../../tiles/DataTiles/CodeTile';
import { 
  InlineTableWrapper, 
  InlineTableHead, 
  InlineTableBody, 
  InlineTableRow, 
  InlineTableCell 
} from '../../tables/InlineTable';
import type {
  MessageProps,
  MessageContentProps,
  MessageActionsProps,
  MessageActionProps,
  MessageResponseProps,
  MessageTextProps,
  MessageAttachmentProps,
  MessageAttachmentsProps,
  MessageToolbarProps,
} from './types';

// ============================================================================
// Message Components
// ============================================================================

/**
 * Message container - displays a single message from user or assistant
 * Handles alignment and styling based on role
 */
export const Message: React.FC<MessageProps> = ({ className, from, children, ...props }) => (
  <div
    className={cn(
      'core-message',
      'group',
      from === 'user' ? 'core-message--user is-user' : 'core-message--assistant is-assistant',
      className
    )}
    {...props}
  >
    {children}
  </div>
);

/**
 * MessageContent - wrapper for message bubble/content area
 * Styles differently based on parent Message role (via group classes)
 */
export const MessageContent: React.FC<MessageContentProps> = ({ children, className, ...props }) => (
  <div
    className={cn('core-message__content', className)}
    {...props}
  >
    {children}
  </div>
);

/**
 * MessageActions - container for action buttons (copy, retry, etc.)
 */
export const MessageActions: React.FC<MessageActionsProps> = ({ className, children, ...props }) => (
  <div className={cn('core-message__actions', className)} {...props}>
    {children}
  </div>
);

/**
 * MessageAction - individual action button with optional tooltip
 */
export const MessageAction: React.FC<MessageActionProps> = ({
  tooltip,
  children,
  label,
  className,
  color: _color,
  ...props
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isHovered, setIsHovered] = useState(false);

  const button = (
    <Tag
      template="action-sm"
      ref={buttonRef}
      className={className}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      {...props}
    >
      {children}
      <span className="core-visually-hidden">{label || tooltip}</span>
    </Tag>
  );

  if (tooltip) {
    return (
      <>
        {button}
        <Popover
          isVisible={isHovered}
          targetRef={buttonRef}
          content={tooltip}
          position="above"
          showArrow={true}
        />
      </>
    );
  }

  return button;
};

/**
 * Default custom components for LLM-generated content
 * Matches MDX documentation renderer patterns exactly
 */
const defaultCustomComponents = {
  // Override Streamdown's Tailwind-based components with semantic HTML
  strong: ({ children }: any) => <strong>{children}</strong>,
  em: ({ children }: any) => <em>{children}</em>,
  
  // Links → Link component with dotted underline (matches MDX but dotted)
  a: ({ href, children, ...props }: any) => (
    <Link href={href} size="medium" color="strong" underline="dotted" {...props}>
      {children}
    </Link>
  ),
  
  // Code blocks → CodeTile (matches MDX pattern)
  pre: ({ children, ...props }: any) => {
    const rawCode = typeof children === 'string' ? children : children?.props?.children;
    const code = typeof rawCode === 'string' ? rawCode.trim() : rawCode;
    const language = children?.props?.className?.replace('language-', '') || 'text';
    
    return (
      <CodeTile 
        code={code || ''} 
        language={language}
        showLineNumbers={true}
        showCopyButton={true}
        hideHeader={true}
        hideFooter={true}
        {...props}
      />
    );
  },
  
  code: ({ children, className, ...props }: any) => {
    if (className && className.startsWith('language-')) {
      const language = className.replace('language-', '');
      const code = typeof children === 'string' ? children.trim() : children;
      return (
        <CodeTile 
          code={code || ''} 
          language={language}
          showLineNumbers={true}
          showCopyButton={true}
          hideHeader={true}
          hideFooter={true}
          {...props}
        />
      );
    }
    return <code {...props}>{children}</code>;
  },
  
  // Tables → InlineTable minimal (clean borderless variant)
  table: ({ children, ...props }: any) => {
    return (
      <InlineTableWrapper aria-label="Chat table" variant="minimal" {...props}>
        {children}
      </InlineTableWrapper>
    );
  },
  
  thead: ({ children, ...props }: any) => (
    <InlineTableHead {...props}>
      {children}
    </InlineTableHead>
  ),
  
  tbody: ({ children, ...props }: any) => (
    <InlineTableBody {...props}>
      {children}
    </InlineTableBody>
  ),
  
  tr: ({ children, ...props }: any) => {
    // Detect if this is a header row by checking if children are th elements
    const childArray = React.Children.toArray(children);
    const isHeaderRow = childArray.some(
      (child) => React.isValidElement(child) && child.type === 'th'
    );
    
    return (
      <InlineTableRow head={isHeaderRow} {...props}>
        {children}
      </InlineTableRow>
    );
  },
  
  th: ({ children, ...props }: any) => (
    <InlineTableCell head {...props}>
      {children}
    </InlineTableCell>
  ),
  
  td: ({ children, ...props }: any) => (
    <InlineTableCell {...props}>
      {children}
    </InlineTableCell>
  ),
  
  // Map <div data-component="data-tile"> to DataTile
  div: ({ children, 'data-component': component, 'data-items': items, ...props }: any) => {
    if (component === 'data-tile' && items) {
      try {
        const parsedItems = typeof items === 'string' ? JSON.parse(items) : items;
        return <DataTile items={parsedItems} />;
      } catch (e) {
        return <div {...props}>{children}</div>;
      }
    }
    
    if (component === 'info-tile') {
      return <InfoTile {...props}>{children}</InfoTile>;
    }
    
    return <div {...props}>{children}</div>;
  },
};

/**
 * MessageResponse - renders markdown content with streaming support
 * Uses Streamdown for handling incomplete/streaming markdown from AI
 * Overrides Streamdown's Tailwind-based components with semantic HTML
 * 
 * Supports custom component syntax for LLMs:
 * - <div data-component="data-tile" data-items='[...]'></div>
 * - <div data-component="info-tile">Content</div>
 * - Blockquotes with special markers: > [!INFO], > [!WARNING], > [!NOTE]
 */
export const MessageResponse = memo<MessageResponseProps>(
  ({ className, children, isAnimating, customComponents, ...props }) => (
    <Streamdown
      className={cn('core-message__response', className)}
      parseIncompleteMarkdown={true}
      isAnimating={isAnimating}
      components={{
        ...defaultCustomComponents,
        // Allow additional custom components to be injected
        ...customComponents,
      }}
      {...props}
    >
      {String(children || '')}
    </Streamdown>
  ),
  (prevProps, nextProps) => 
    prevProps.children === nextProps.children && 
    prevProps.isAnimating === nextProps.isAnimating &&
    prevProps.customComponents === nextProps.customComponents
);

MessageResponse.displayName = 'MessageResponse';

/**
 * MessageText - renders plain text content for user messages
 * No markdown parsing, just displays text as-is
 */
export const MessageText: React.FC<MessageTextProps> = ({ className, children, ...props }) => (
  <div
    className={cn('core-message__text', className)}
    {...props}
  >
    {children}
  </div>
);

/**
 * MessageAttachment - displays a single file attachment
 * Shows preview for images, icon for other files
 */
export function MessageAttachment({
  data,
  className,
  onRemove,
  ...props
}: MessageAttachmentProps) {
  const attachmentRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const filename = data.filename || '';
  const mediaType =
    data.mediaType?.startsWith('image/') && data.url ? 'image' : 'file';
  const isImage = mediaType === 'image';
  const attachmentLabel = filename || (isImage ? 'Image' : 'Attachment');

  return (
    <>
      <div
        ref={attachmentRef}
        className={cn('core-message__attachment', className)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        {...props}
      >
        {isImage ? (
          <>
            <img
              alt={filename || 'attachment'}
              className="core-message__attachment-image"
              height={100}
              src={data.url}
              width={100}
            />
            {onRemove && (
              <Tag
                template="action-sm"
                className="core-message__attachment-remove"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove();
                }}
              >
                <CloseOutline size={12} />
                <span className="core-visually-hidden">Remove</span>
              </Tag>
            )}
          </>
        ) : (
          <>
            <div className="core-message__attachment-file-icon">
              <Attachment size={16} />
            </div>
            {onRemove && (
              <Tag
                template="action-sm"
                className="core-message__attachment-remove"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove();
                }}
              >
                <CloseOutline size={12} />
                <span className="core-visually-hidden">Remove</span>
              </Tag>
            )}
          </>
        )}
      </div>
      
      {/* Hover preview popover */}
      <Popover
        isVisible={isHovered}
        targetRef={attachmentRef}
        content={
          <div className="core-message__attachment-preview-content">
            {isImage && data.url && (
              <div className="core-message__attachment-preview-image">
                <img
                  alt={filename || 'attachment preview'}
                  height={384}
                  src={data.url}
                  width={448}
                />
              </div>
            )}
            <div className="core-message__attachment-preview-info">
              <h4 className="core-message__attachment-preview-title">
                {filename || (isImage ? 'Image' : 'Attachment')}
              </h4>
              {data.mediaType && (
                <p className="core-message__attachment-preview-type">
                  {data.mediaType}
                </p>
              )}
            </div>
          </div>
        }
        position="above"
        showArrow={true}
        className="core-message__attachment-preview"
      />
    </>
  );
}

/**
 * MessageAttachments - container for multiple attachments
 */
export function MessageAttachments({
  children,
  className,
  ...props
}: MessageAttachmentsProps) {
  if (!children) {
    return null;
  }

  return (
    <div
      className={cn('core-message__attachments', className)}
      {...props}
    >
      {children}
    </div>
  );
}

/**
 * MessageToolbar - toolbar at the bottom of message
 */
export const MessageToolbar: React.FC<MessageToolbarProps> = ({
  className,
  children,
  ...props
}) => (
  <div
    className={cn('core-message__toolbar', className)}
    {...props}
  >
    {children}
  </div>
);
